import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import LocationsAPI, { LocationRaw } from 'services/API/LocationsAPI';
import { COUNTRY_NAMES } from 'utils/constants/location';
import { cityMap } from 'utils/misc';

type TExpectedCountryNames = 'CANADA' | 'USA';

export const useLocations = () => {
  const { data } = useQuery<LocationRaw[]>(
    ['locations-markets'],
    async () => {
      const result = await LocationsAPI.getLocations();

      return result?.data || [];
    },
    { refetchOnWindowFocus: false },
  );

  const marketsData: LocationRaw[] = data || [];

  const getCountryName = (countryName: string) => {
    if (!(countryName.toUpperCase() in COUNTRY_NAMES)) {
      return 'Other';
    }

    return COUNTRY_NAMES[countryName.toUpperCase() as TExpectedCountryNames];
  };

  const allCities = useMemo(() => {
    const temp = marketsData
      .map(({ states, name: country }) =>
        states.map(({ metropolitans, alpha }) =>
          metropolitans.map((region) =>
            (region.counties || []).map((county) =>
              (county.cities || []).map((city) => ({
                ...city,
                countyId: county.id,
                countyName: county.name,
                country: getCountryName(country),
                state: alpha,
              })),
            ),
          ),
        ),
      )
      .flat()
      .flat()
      .flat();

    return temp.flat();
  }, [data]);

  const allRegions = useMemo(() => {
    const getRegionCities = (
      region: LocationRaw['states'][number]['metropolitans'][number],
    ) => (region.counties || []).flatMap((county) => county.cities || []);

    const temp = marketsData.reduce<Record<string, number[]>>((acc, { states }) => {
      states.forEach(({ metropolitans }) =>
        metropolitans.forEach((region) => {
          acc[region.code] = getRegionCities(region).map((city) => city.id);
        }),
      );

      return acc;
    }, {});

    return temp;
  }, [data]);

  const cityToRegionMap = useMemo(() => {
    const getRegionCities = (
      region: LocationRaw['states'][number]['metropolitans'][number],
    ) => (region.counties || []).flatMap((county) => county.cities || []);

    const temp = marketsData.reduce<Record<number, string>>((acc, { states }) => {
      states.map(({ metropolitans }) =>
        metropolitans.map((region) =>
          getRegionCities(region).forEach((city) => {
            acc[city.id] = region.code;
          }),
        ),
      );

      return acc;
    }, {});

    return temp;
  }, [data]);

  const getCities = useCallback(
    (...ids: number[]) =>
      ids.length ? allCities.filter(({ id }) => ids.includes(id)) : allCities,
    [allCities],
  );

  const filteredCities = useMemo(() => {
    // Remove LA and SF from ProjectGroupEdit
    return getCities().filter(
      (city) => city.id !== cityMap.LOS_ANGELES && city.id !== cityMap.SAN_FRANCISCO,
    );
  }, [getCities]);

  return {
    getCities,
    filteredCities,
    allRegions,
    cityToRegionMap,
  };
};
